import DrawerPanel from '@/components/drawerPanel';
import EntitySettings from '@/components/entities/configuration';
import { EntityType } from '@/components/entities/entityType';
import SettingsValidator from '@/components/entities/settingsValidators';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import DeleteIcon from '@mui/icons-material/Delete';
import LaunchIcon from '@mui/icons-material/Launch';
import SettingsIcon from '@mui/icons-material/Settings';
import ErrorIcon from '@mui/icons-material/Error';
import { Box, ButtonGroup, IconButton, Link, Paper, Stack, Tooltip, Typography, useTheme, useMediaQuery } from '@mui/material';
import { Handle, NodeProps, NodeToolbar, Position, useReactFlow } from '@xyflow/react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useConfirm } from 'material-ui-confirm';
import { useApiContext } from '@/contexts/apiContext';
import { AppNodeType } from './nodesUtils';

const settingsValidator = new SettingsValidator();

type Props = { node: NodeProps<AppNodeType>; icon: any; label: string; canDelete?: boolean };

const BaseNode = ({ node, icon, label, canDelete = false }: Props) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { t } = useTranslation();
  const confirm = useConfirm();
  const apiContext = useApiContext();
  const { fitView, setCenter, getNode, getViewport, setViewport } = useReactFlow();
  const prevViewport = useRef<{ x: number; y: number; zoom: number } | null>(null);

  const [hasSourceHandle, setHasSourceHandle] = useState(true);
  const [hasTargetHandle, setHasTargetHandle] = useState(true);

  const [showSettings, setShowSettings] = useState(false);
  const [settingsError, setSettingsError] = useState(false);
  const [entityLink, setEntityLink] = useState('');
  const [defName, setDefName] = useState('');
  const [isHovered, setIsHovered] = useState(false);

  const nodeType = node.data.entity?.entityType;
  const isAgent = nodeType === EntityType.agentInstance;
  const isDataSource = nodeType === EntityType.dataSourceInstance;
  const isUserApp = nodeType === EntityType.userAppInstance;

  const accentColor = isAgent ? theme.palette.secondary.main : isDataSource ? theme.palette.primary.main : '#3b82f6'; // App accent blue

  useEffect(() => {
    const entity = node.data.entity;
    if (entity) {
      let entityDef =
        entity.entityType === EntityType.dataSourceInstance
          ? entity.dataSourceDef
          : entity.entityType === EntityType.agentInstance
            ? entity.agentDef
            : entity.userAppDef;

      const defId =
        entityDef?.id ||
        entity.entityDefId ||
        (entity.entityType === EntityType.dataSourceInstance
          ? entity.dataSourceDefId
          : entity.entityType === EntityType.agentInstance
            ? entity.agentDefId
            : entity.userAppDefId);

      if (defId && entity.id) {
        if (entity.entityType === EntityType.dataSourceInstance) {
          setEntityLink(`/dataSourceInstance/${defId}/${entity.id}`);
        } else if (entity.entityType === EntityType.agentInstance) {
          setEntityLink(`/agentInstance/${defId}/${entity.id}`);
        } else if (entity.entityType === EntityType.userAppInstance) {
          setEntityLink(`/userAppInstance/${defId}/${entity.id}`);
        }
      }

      if (entityDef) {
        const { consumedEvents = [], emittedEventDefs = [], settingDefs = [] } = entityDef;
        const { sourceEntities = [], settings = {} } = entity;
        const [, hasError = false] = settingsValidator.validateSettings(settingDefs, settings, t) || [];
        setSettingsError(hasError);
        setHasTargetHandle(consumedEvents.length > 0 || sourceEntities.length > 0);
        setHasSourceHandle(emittedEventDefs.length > 0);
        setDefName(entityDef.name);
      } else if (defId) {
        (async () => {
          try {
            let fetchedDef = null;
            if (entity.entityType === EntityType.dataSourceInstance) {
              fetchedDef = await apiContext.getDataSourceDef(defId);
              if (fetchedDef) entity.dataSourceDef = fetchedDef;
            } else if (entity.entityType === EntityType.agentInstance) {
              fetchedDef = await apiContext.getAgentDef(defId);
              if (fetchedDef) entity.agentDef = fetchedDef;
            } else if (entity.entityType === EntityType.userAppInstance) {
              fetchedDef = await apiContext.getUserAppDef(defId);
              if (fetchedDef) entity.userAppDef = fetchedDef;
            }
            if (fetchedDef) {
              const { consumedEvents = [], emittedEventDefs = [], settingDefs = [] } = fetchedDef;
              const { sourceEntities = [], settings = {} } = entity;
              const [, hasError = false] = settingsValidator.validateSettings(settingDefs, settings, t) || [];
              setSettingsError(hasError);
              setHasTargetHandle(consumedEvents.length > 0 || sourceEntities.length > 0);
              setHasSourceHandle(emittedEventDefs.length > 0);
              setDefName(fetchedDef.name);
            }
          } catch (err) {
            console.error('Failed to load entity definition', err);
          }
        })();
      }
    }
  }, [node.data.entity, apiContext, t]);

  useEffect(() => {
    if (showSettings) {
      // Capture current viewport before moving
      prevViewport.current = getViewport();

      const currentNode = getNode(node.id);
      if (currentNode && currentNode.position) {
        // Shift center to the right so node appears on the left (uncovered area)
        const offset = isMobile ? 0 : window.innerWidth / 3;
        setCenter(currentNode.position.x + offset, currentNode.position.y, { zoom: 1, duration: 800 });
      }
    } else if (prevViewport.current) {
      setViewport(prevViewport.current, { duration: 800 });
      prevViewport.current = null;
    }
  }, [showSettings, node.id, getNode, setCenter, getViewport, setViewport, isMobile]);

  const onSettingsClick = useCallback(() => {
    setShowSettings(true);
  }, []);

  const hideSettings = () => {
    setShowSettings(false);
  };

  const updateEntity = async (entity: any): Promise<void> => {
    if (node.data.updateNode) {
      node.data.entity = entity;
      await node.data.updateNode(entity);
      hideSettings();
    }
  };

  return (
    <>
      <Paper
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        elevation={0}
        sx={{
          p: 1,
          minWidth: 160,
          maxWidth: 220,
          borderRadius: '16px',
          bgcolor: 'background.paper',
          border: '1px solid',
          borderColor: node.selected
            ? theme.palette.secondary.main
            : settingsError
              ? theme.palette.error.main
              : 'divider',
          borderTop: `4px solid ${accentColor}`,
          transition: 'all 0.2s ease-in-out',
          boxShadow: node.selected
            ? `0 0 15px ${theme.palette.secondary.main}aa`
            : settingsError
              ? `0 0 10px ${theme.palette.error.main}33`
              : 'none',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: node.selected
              ? `0 0 20px ${theme.palette.secondary.main}bb`
              : settingsError
                ? `0 4px 15px ${theme.palette.error.main}55`
                : '0 6px 16px rgba(0,0,0,0.08)',
          },
          overflow: 'visible',
          position: 'relative',
        }}
      >
        {settingsError && (
          <Tooltip title="Invalid configuration. Click the configuration icon below to fix it.">
            <Box
              sx={{
                position: 'absolute',
                top: -10,
                right: -10,
                bgcolor: 'error.main',
                color: 'error.contrastText',
                borderRadius: '50%',
                width: 20,
                height: 20,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: 2,
                zIndex: 5,
                animation: 'pulse 2s infinite',
                '@keyframes pulse': {
                  '0%': {
                    boxShadow: '0 0 0 0px rgba(211, 47, 47, 0.5)',
                  },
                  '70%': {
                    boxShadow: '0 0 0 6px rgba(211, 47, 47, 0)',
                  },
                  '100%': {
                    boxShadow: '0 0 0 0px rgba(211, 47, 47, 0)',
                  },
                },
              }}
            >
              <ErrorIcon sx={{ fontSize: '1rem' }} />
            </Box>
          </Tooltip>
        )}
        <Stack spacing={0.5}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography
              variant="caption"
              sx={{
                fontSize: '0.65rem',
                fontWeight: 800,
                color: accentColor,
                letterSpacing: '0.05em',
              }}
            >
              {defName ? defName.toUpperCase() : isAgent ? 'AGENT' : isDataSource ? 'DATA SOURCE' : 'APPLICATION'}
            </Typography>
            <FontAwesomeIcon icon={icon} style={{ fontSize: 14, color: accentColor, opacity: 0.8 }} />
          </Stack>

          <Typography
            variant="body2"
            sx={{
              fontWeight: 700,
              color: 'text.primary',
              fontSize: '0.85rem',
              lineHeight: 1.2,
              mb: 0.5,
            }}
          >
            {node.data.name || node.data.entity?.name}
          </Typography>

          {isAgent && (
            <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mb: 0.25 }} flexWrap="wrap">
              {(!node.data.entity?.settings?.executionMode || node.data.entity?.settings?.executionMode === 'standard') ? (
                <Box
                  sx={{
                    px: 0.75,
                    py: 0.15,
                    borderRadius: '4px',
                    bgcolor: '#eff6ff',
                    border: '1px solid #bfdbfe',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.5,
                  }}
                >
                  <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: '#2563eb' }} />
                  <Typography variant="caption" sx={{ fontSize: '0.6rem', fontWeight: 700, color: '#1d4ed8' }}>
                    STANDARD STEP
                  </Typography>
                </Box>
              ) : (
                <Box
                  sx={{
                    px: 0.75,
                    py: 0.15,
                    borderRadius: '4px',
                    bgcolor: '#f5f3ff',
                    border: '1px solid #ddd6fe',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.5,
                  }}
                >
                  <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: '#7c3aed' }} />
                  <Typography variant="caption" sx={{ fontSize: '0.6rem', fontWeight: 700, color: '#6d28d9' }}>
                    {node.data.entity?.settings?.executionMode === 'swarm_supervisor'
                      ? 'SWARM SUPERVISOR'
                      : 'ReAct AUTONOMOUS'}
                  </Typography>
                </Box>
              )}
            </Stack>
          )}

          {node.data.currentEntityId && node.data.entityNode && node.data.currentEntityId === node.data.entity?.id && (
            <Box
              sx={{
                px: 1,
                py: 0.25,
                bgcolor: 'primary.main',
                borderRadius: '4px',
                width: 'fit-content',
              }}
            >
              <Typography variant="caption" sx={{ color: 'white', fontSize: '0.6rem', fontWeight: 700 }}>
                CURRENT ENTITY
              </Typography>
            </Box>
          )}
        </Stack>

        {hasTargetHandle && (
          <Handle
            id={`${node.id}_target_handle_a`}
            type="target"
            position={Position.Left}
            style={{
              width: '12px',
              height: '12px',
              background: accentColor,
              border: '2px solid white',
              boxShadow: '0 0 5px rgba(0,0,0,0.1)',
              left: -6,
              transition: 'transform 0.2s ease',
            }}
          />
        )}
        {hasSourceHandle && (
          <Handle
            id={`${node.id}_source_handle_a`}
            type="source"
            position={Position.Right}
            style={{
              width: '12px',
              height: '12px',
              background: accentColor,
              border: '2px solid white',
              boxShadow: '0 0 5px rgba(0,0,0,0.1)',
              right: -6,
              transition: 'transform 0.2s ease',
            }}
            isConnectableStart={false}
          />
        )}
      </Paper>

      {(!node.data.currentEntityId || (node.data.entityNode && node.data.currentEntityId !== node.data.entity?.id)) && (
        <NodeToolbar isVisible={true} position={Position.Bottom} offset={0} align="end">
          <Box
            sx={{
              display: 'flex',
              gap: 0.25,
              p: 0.3,
              bgcolor: 'background.paper',
              borderRadius: '20px',
              border: 1,
              borderColor: 'divider',
              boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
              transition: 'all 0.2s ease',
            }}
          >
            {Boolean(entityLink) && node.data.entity?.id && node.data.entity?.createdDate && (
              <Link href={entityLink} target="_blank">
                <Tooltip title="View Details">
                  <IconButton
                    size="small"
                    sx={{
                      p: 0.4,
                      color: 'primary.main',
                      '&:hover': { bgcolor: 'primary.light' },
                    }}
                  >
                    <LaunchIcon fontSize="inherit" style={{ fontSize: '1.1rem' }} />
                  </IconButton>
                </Tooltip>
              </Link>
            )}
            {!node.data.viewOnly && (
              <Tooltip title="Configuration">
                <IconButton
                  size="small"
                  sx={{
                    p: 0.4,
                    color: 'primary.main',
                    '&:hover': { bgcolor: 'primary.light' },
                  }}
                  onClick={onSettingsClick}
                >
                  <SettingsIcon fontSize="inherit" style={{ fontSize: '1.1rem' }} />
                </IconButton>
              </Tooltip>
            )}
            {canDelete && !node.data.viewOnly && (
              <Tooltip title="Delete Node">
                <IconButton
                  size="small"
                  sx={{
                    p: 0.4,
                    color: 'error.main',
                    '&:hover': { bgcolor: 'Red.Lightest' },
                  }}
                  onClick={() => {
                    confirm({ description: 'Are you sure you want to delete this node?' }).then(() => {
                      if (node.data.deleteNode) {
                        node.data.deleteNode(node.id);
                      }
                    });
                  }}
                >
                  <DeleteIcon fontSize="inherit" style={{ fontSize: '1.1rem' }} />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </NodeToolbar>
      )}

      {showSettings && (
        <DrawerPanel
          open={showSettings}
          onClose={hideSettings}
          minWidth={isMobile ? '100vw' : '50vw'}
          title={
            node.data.entity?.dataSourceDef?.name ||
            node.data.entity?.agentDef?.name ||
            node.data.entity?.userAppDef?.name
          }
        >
          <EntitySettings
            getNewEntities={node.data.getNewEntities}
            getAllEntities={node.data.getAllEntities}
            entity={node.data.entity}
            onCancel={hideSettings}
            onSubmit={updateEntity}
            isNew={!node.data.entity.id}
          />
        </DrawerPanel>
      )}
    </>
  );
};

export default BaseNode;
